import { type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import {
	Box,
	Flex,
	Input,
	MenuItem,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	Text,
	Tooltip,
	VStack,
	useDisclosure,
	type BoxProps,
	type PlacementWithLogical,
} from '@chakra-ui/react';
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { brand, neutral } from '../theme/tokens';
import { Button } from './Button';
import { Icon } from './Icon';

/**
 * Searchable checkbox menu — variant 1e "Long lists (OEMs, carriers) — tokens + search".
 *
 * ENG-2145: popover is a hairline border only (no drop shadow).
 * Color comes from the Yaxle brand / neutral scales — no raw hex in this file.
 */

export const checkboxMenuColors = {
	brand: brand[500],
	chipBg: brand[50],
	chipBorder: brand[100],
	selectedRowBg: brand[50],
	hoverRowBg: neutral[50],
	divider: neutral[50],
	popoverBorder: neutral[100],
	uncheckedBorder: neutral[300],
	icon: neutral[400],
	muted: neutral[500],
	rowLabel: neutral[700],
	selectedLabel: neutral[800],
	input: neutral[800],
} as const;

export const checkboxMenuFocusRing = '0 0 0 3px rgba(0, 113, 227, 0.12)';

/**
 * Shared toolbar control metrics — Location trigger, search, icon buttons.
 * Popover content (rows, search, FilterBar labels) references `fontSize` from
 * here too, so the menu can never render at a different scale than its trigger.
 */
export const checkboxMenuTriggerMetrics = {
	minH: '2.5rem',
	px: '0.625rem',
	py: '0.25rem',
	borderRadius: 'md',
	fontSize: '0.8125rem',
	fontWeight: 500,
} as const;

export const checkboxMenuLabelSx = {
	fontSize: '0.625rem',
	fontWeight: 600,
	lineHeight: 1,
	letterSpacing: '0.07em',
	color: checkboxMenuColors.muted,
	mb: '0.5rem',
	p: 0,
} as const;

export const checkboxMenuPopoverContentSx = {
	p: 0,
	border: `1px solid ${checkboxMenuColors.popoverBorder}`,
	borderRadius: 'lg',
	bg: 'white',
	overflow: 'hidden',
	boxShadow: 'none',
	_focus: { boxShadow: 'none' },
	_focusVisible: { boxShadow: 'none' },
} as const;

/**
 * Same chrome as `checkboxMenuPopoverContentSx` for menus that position
 * themselves (absolute `Box`) instead of going through Chakra's Popover.
 */
export const menuSurfaceSx = {
	border: `1px solid ${checkboxMenuColors.popoverBorder}`,
	borderRadius: 'lg',
	bg: 'white',
	overflow: 'hidden',
	boxShadow: 'none',
} as const;

export function CheckboxMenuLabel({ children }: { children: ReactNode }) {
	return (
		<Text as="div" sx={checkboxMenuLabelSx}>
			{children}
		</Text>
	);
}

export function TokenChip({
	children,
	onRemove,
	fill = false,
}: {
	children: ReactNode;
	/** Product remove affordance — omitted in variant 1e; keep when callers already have it. */
	onRemove?: () => void;
	/** Stretch to the container width instead of hugging the label. */
	fill?: boolean;
}) {
	return (
		<Flex
			as="span"
			align="center"
			justify={fill && onRemove ? 'space-between' : undefined}
			flex={fill ? '1' : undefined}
			minW={fill ? 0 : undefined}
			gap="4px"
			fontSize="11.5px"
			fontWeight={500}
			lineHeight="11.5px"
			color={checkboxMenuColors.brand}
			bg={checkboxMenuColors.chipBg}
			border="1px solid"
			borderColor={checkboxMenuColors.chipBorder}
			borderRadius="5px"
			px="7px"
			py="4px"
			whiteSpace="nowrap"
			overflow={fill ? 'hidden' : undefined}
		>
			{fill ? (
				<Box as="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
					{children}
				</Box>
			) : (
				children
			)}
			{onRemove ? (
				<Box
					as="button"
					type="button"
					aria-label="Remove"
					lineHeight={0}
					color={checkboxMenuColors.brand}
					onClick={(e: MouseEvent) => {
						e.stopPropagation();
						onRemove();
					}}
				>
					<Icon name="x" size="0.5rem" strokeWidth={2} />
				</Box>
			) : null}
		</Flex>
	);
}

function ChevronIcon({ open }: { open: boolean }) {
	return (
		<Icon
			name="chevronUp"
			size="sm"
			color={checkboxMenuColors.icon}
			transform={open ? undefined : 'rotate(180deg)'}
		/>
	);
}

function SearchGlyph() {
	return <Icon name="search" size="sm" color={checkboxMenuColors.icon} />;
}

export function CheckboxMenuCheck({ isSelected }: { isSelected: boolean }) {
	return (
		<Flex
			w="16px"
			h="16px"
			minW="16px"
			borderRadius="5px"
			boxSizing="border-box"
			align="center"
			justify="center"
			flexShrink={0}
			bg={isSelected ? checkboxMenuColors.brand : 'white'}
			color="fg.onBrand"
			border={isSelected ? `1px solid ${checkboxMenuColors.brand}` : `1.5px solid ${checkboxMenuColors.uncheckedBorder}`}
		>
			{isSelected ? <Icon name="check" size="0.625rem" strokeWidth={3.2} /> : null}
		</Flex>
	);
}

/**
 * Names behind a collapsed count ("6 selected", "Makes: 2 selected").
 *
 * The panel is pinned to the anchor's width so it reads as an extension of the
 * control rather than a floating box, which means the width has to be measured
 * — a CSS-only match isn't possible across the portal boundary.
 */
export type SelectionSummaryTooltipProps = Omit<BoxProps, 'children' | 'placement'> & {
	items: string[];
	children: ReactNode;
	isDisabled?: boolean;
	placement?: PlacementWithLogical;
	/** Fill the parent (a full-width trigger) instead of hugging (a filter chip). */
	fillParent?: boolean;
};

export const SelectionSummaryTooltip = forwardRef<
	HTMLDivElement,
	SelectionSummaryTooltipProps
>(function SelectionSummaryTooltip(
	{ items, children, isDisabled, placement = 'bottom-end', fillParent = false, ...rest },
	ref,
) {
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const [anchorWidth, setAnchorWidth] = useState(0);

	// The anchor is often a Popover trigger, so the ref has to reach the DOM node
	// while still feeding the measurement.
	const setAnchor = (node: HTMLDivElement | null) => {
		anchorRef.current = node;
		if (typeof ref === 'function') ref(node);
		else if (ref) ref.current = node;
	};

	useLayoutEffect(() => {
		if (anchorRef.current) setAnchorWidth(anchorRef.current.offsetWidth);
	}, [children]);

	useEffect(() => {
		const node = anchorRef.current;
		if (!node || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(([entry]) => {
			setAnchorWidth(Math.round(entry.contentRect.width));
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	const width = anchorWidth > 0 ? `${anchorWidth}px` : undefined;

	return (
		<Tooltip
			label={
				<VStack align="stretch" spacing="2px" maxH="240px" overflowY="auto">
					{items.map((item, index) => (
						<Text key={`${item}-${index}`} fontSize="xs" lineHeight="short">
							{item}
						</Text>
					))}
				</VStack>
			}
			isDisabled={isDisabled || items.length === 0}
			placement={placement}
			openDelay={200}
			hasArrow
			w={width}
			maxW={width}
			borderRadius="md"
			px="10px"
			py="8px"
		>
			<Box
				{...rest}
				ref={setAnchor}
				w={fillParent ? '100%' : undefined}
				display={fillParent ? undefined : 'inline-flex'}
			>
				{children}
			</Box>
		</Tooltip>
	);
});

export function CheckboxMenuTrigger({
	isOpen,
	tokens,
	emptyLabel,
	emptyLabelColor,
	triggerPrefix,
	onRemoveToken,
	fillToken = false,
	...rest
}: {
	isOpen?: boolean;
	tokens?: string[];
	emptyLabel?: string;
	/** Form fields pass a muted tone so the empty label reads as a placeholder. */
	emptyLabelColor?: string;
	/** Text shown before the tokens when at least one is selected. */
	triggerPrefix?: string;
	onRemoveToken?: (index: number) => void;
	/**
	 * Let a lone token stretch to the trigger width, so a collapsed summary
	 * ("6 selected") reads as the field's value rather than a floating chip.
	 * Off for single-select pickers, where a full-width chip would look like a
	 * pressed state instead of a chosen value.
	 */
	fillToken?: boolean;
} & Record<string, unknown>) {
	const hasTokens = Boolean(tokens && tokens.length > 0);
	const isLoneToken = tokens?.length === 1;
	return (
		<Flex
			as="button"
			type="button"
			align="center"
			gap="8px"
			minH={checkboxMenuTriggerMetrics.minH}
			w="100%"
			px={checkboxMenuTriggerMetrics.px}
			py={checkboxMenuTriggerMetrics.py}
			border="1px solid"
			borderColor={isOpen ? checkboxMenuColors.brand : checkboxMenuColors.popoverBorder}
			borderRadius="8px"
			bg="white"
			boxShadow={isOpen ? checkboxMenuFocusRing : 'none'}
			cursor="pointer"
			textAlign="left"
			_hover={{ borderColor: checkboxMenuColors.brand }}
			_focusVisible={{
				borderColor: checkboxMenuColors.brand,
				boxShadow: checkboxMenuFocusRing,
				outline: 'none',
			}}
			{...rest}
		>
			<Flex flex="1" wrap="wrap" gap="5px" minW={0} align="center">
				{hasTokens && triggerPrefix ? (
					<Text
						fontSize={checkboxMenuTriggerMetrics.fontSize}
						fontWeight={checkboxMenuTriggerMetrics.fontWeight}
						lineHeight="13px"
						color={checkboxMenuColors.input}
						py="4px"
						flexShrink={0}
					>
						{triggerPrefix}
					</Text>
				) : null}
				{hasTokens
					? tokens!.map((token, i) => (
							<TokenChip
								key={`${token}-${i}`}
								fill={fillToken && isLoneToken}
								onRemove={onRemoveToken ? () => onRemoveToken(i) : undefined}
							>
								{token}
							</TokenChip>
						))
					: (
							<Text
								fontSize={checkboxMenuTriggerMetrics.fontSize}
								fontWeight={checkboxMenuTriggerMetrics.fontWeight}
								lineHeight="13px"
								color={emptyLabelColor ?? checkboxMenuColors.input}
								px={0}
								py="4px"
							>
								{emptyLabel ?? 'Select'}
							</Text>
						)}
			</Flex>
			<ChevronIcon open={Boolean(isOpen)} />
		</Flex>
	);
}

export function CheckboxMenuSearch({
	value,
	onChange,
	placeholder,
	autoFocus,
}: {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	autoFocus?: boolean;
}) {
	return (
		<Flex
			align="center"
			gap="8px"
			p="8px"
			borderBottom="1px solid"
			borderColor={checkboxMenuColors.divider}
		>
			<SearchGlyph />
			<Input
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder}
				autoFocus={autoFocus}
				variant="unstyled"
				fontSize={checkboxMenuTriggerMetrics.fontSize}
				fontWeight={400}
				lineHeight="13px"
				color={checkboxMenuColors.input}
				bg="transparent"
				border="0"
				outline="none"
				px="0"
				h="auto"
				_placeholder={{ color: checkboxMenuColors.icon }}
				_focus={{ boxShadow: 'none', outline: 'none' }}
			/>
		</Flex>
	);
}

/**
 * The one menu row.
 *
 * Geometry never changes — 34px tall, 7px radius, `brand.50` when selected,
 * `neutral.50` on hover. Only the glyph carries meaning:
 *
 * - `multi`   — square checkbox on the left. Toggles, stays open.
 * - `single`  — check on the right. Picks one of a set.
 * - `drillIn` — chevron on the right. Navigates into a sub-panel, never tinted,
 *               because a check here would read as "selected" (ENG-2312).
 *
 * Do not hand-roll a selected row. If a menu needs a new affordance, add a kind
 * here so every surface inherits it.
 */
export type MenuRowKind = 'multi' | 'single' | 'drillIn';

export interface MenuRowProps {
	children: ReactNode;
	onClick: () => void;
	kind?: MenuRowKind;
	isSelected?: boolean;
	isDisabled?: boolean;
	/** Leading glyph. Muted when unselected, brand when selected. */
	icon?: ReactNode;
	/** Trailing value preview on a `drillIn` row (e.g. "3 selected"). */
	summary?: ReactNode;
	/**
	 * Render as a plain `div` when an ancestor already owns the button
	 * semantics (Chakra `MenuItem`). Prefer `MenuItemRow` over setting this.
	 */
	as?: 'button' | 'div';
	/**
	 * Sentence-length labels (settings rule choices) wrap instead of
	 * truncating; the row grows past 34px to fit.
	 */
	allowWrap?: boolean;
	/** Muted second line under the label (an address, a hint). */
	description?: ReactNode;
	/**
	 * Hover tint override for rows on a tinted surface (e.g. the gray account
	 * panel), where the default `neutral.50` wash would be invisible.
	 */
	hoverBg?: string;
}

export function MenuRow({
	children,
	onClick,
	kind = 'multi',
	isSelected = false,
	isDisabled,
	icon,
	summary,
	as = 'button',
	allowWrap = false,
	description,
	hoverBg,
}: MenuRowProps) {
	// A drill-in row is navigation, not state, so it never takes the tint.
	const isTinted = isSelected && kind !== 'drillIn';
	const semantics =
		as === 'button' ? ({ as: 'button', type: 'button' } as const) : ({ as: 'div' } as const);
	const hasDescription = description != null && description !== '';
	const isTall = allowWrap || hasDescription;

	return (
		<Flex
			{...semantics}
			data-menu-row=""
			data-selected={isTinted ? '' : undefined}
			align="center"
			gap="10px"
			h={isTall ? undefined : '34px'}
			minH="34px"
			py={isTall ? '8px' : undefined}
			// A scrolling menu is a flex column, so without this the rows are
			// squashed to fit `maxH` instead of overflowing into the scrollbar.
			flexShrink={0}
			w="100%"
			px="10px"
			borderRadius="7px"
			cursor={isDisabled ? 'not-allowed' : 'pointer'}
			opacity={isDisabled ? 0.5 : 1}
			bg={isTinted ? checkboxMenuColors.selectedRowBg : 'transparent'}
			_hover={
				isDisabled
					? undefined
					: { bg: isTinted ? checkboxMenuColors.selectedRowBg : (hoverBg ?? checkboxMenuColors.hoverRowBg) }
			}
			onClick={() => {
				if (!isDisabled) onClick();
			}}
		>
			{kind === 'multi' ? <CheckboxMenuCheck isSelected={isSelected} /> : null}
			{icon ? (
				<Flex
					align="center"
					justify="center"
					flexShrink={0}
					lineHeight={0}
					color={isTinted ? checkboxMenuColors.brand : checkboxMenuColors.icon}
				>
					{icon}
				</Flex>
			) : null}
			<Flex direction="column" flex="1" minW={0} align="flex-start" justify="center">
				<Text
					w="100%"
					fontSize={checkboxMenuTriggerMetrics.fontSize}
					fontWeight={isTinted ? 600 : 400}
					// `noOfLines` clips to the line box, so it has to clear descenders.
					lineHeight={1.4}
					color={isTinted ? checkboxMenuColors.selectedLabel : checkboxMenuColors.rowLabel}
					noOfLines={allowWrap ? undefined : 1}
					textAlign="left"
				>
					{children}
				</Text>
				{hasDescription ? (
					<Text
						w="100%"
						fontSize="11px"
						fontWeight={400}
						lineHeight={1.4}
						color={checkboxMenuColors.muted}
						noOfLines={1}
						textAlign="left"
					>
						{description}
					</Text>
				) : null}
			</Flex>
			{kind === 'drillIn' && summary ? (
				<Text
					fontSize="11px"
					fontWeight={500}
					lineHeight={1.4}
					color={checkboxMenuColors.brand}
					noOfLines={1}
					flexShrink={0}
				>
					{summary}
				</Text>
			) : null}
			{kind === 'single' && isSelected ? (
				<Icon name="check" size="0.75rem" strokeWidth={3} color={checkboxMenuColors.brand} />
			) : null}
			{kind === 'drillIn' ? (
				<Icon name="chevronRight" size="sm" color={checkboxMenuColors.icon} />
			) : null}
		</Flex>
	);
}

export function CheckboxMenuRow({
	isSelected,
	onClick,
	children,
	isDisabled,
}: {
	isSelected: boolean;
	onClick: () => void;
	children: ReactNode;
	isDisabled?: boolean;
}) {
	return (
		<MenuRow kind="multi" isSelected={isSelected} onClick={onClick} isDisabled={isDisabled}>
			{children}
		</MenuRow>
	);
}

/**
 * `MenuRow` inside a Chakra `Menu`, which needs `MenuItem` for keyboard nav.
 * The `MenuItem` keeps the semantics and hands all painting to `MenuRow`, so a
 * Chakra menu and a popover menu render the same row.
 */
export function MenuItemRow({ onClick, isDisabled, closeOnSelect = true, ...rowProps }: MenuRowProps & { closeOnSelect?: boolean }) {
	return (
		<MenuItem
			p={0}
			bg="transparent"
			borderRadius="7px"
			isDisabled={isDisabled}
			closeOnSelect={closeOnSelect}
			onClick={onClick}
			_hover={{ bg: 'transparent' }}
			_focus={{ bg: 'transparent' }}
			_active={{ bg: 'transparent' }}
			sx={{
				// Keyboard focus has to reach the painted row, not the wrapper.
				'&:focus [data-menu-row]:not([data-selected])': {
					bg: checkboxMenuColors.hoverRowBg,
				},
			}}
		>
			<MenuRow {...rowProps} as="div" isDisabled={isDisabled} onClick={() => {}} />
		</MenuItem>
	);
}

/**
 * Uppercase group heading inside a menu ("Quote type", "Order attributes").
 */
export function MenuSectionLabel({ children }: { children: ReactNode }) {
	return (
		<Text
			as="div"
			fontSize="0.625rem"
			fontWeight={600}
			lineHeight={1}
			letterSpacing="0.07em"
			textTransform="uppercase"
			color={checkboxMenuColors.muted}
			px="10px"
			pt="10px"
			pb="6px"
		>
			{children}
		</Text>
	);
}

export function CheckboxMenuList({
	children,
	maxH = '236px',
}: {
	children: ReactNode;
	maxH?: CSSProperties['maxHeight'];
}) {
	return (
		<Box p="6px" maxH={maxH} overflowY="auto">
			{children}
		</Box>
	);
}

/**
 * Pinned "All …" row above a hairline divider. Stays out of the scrolling list.
 */
export function CheckboxMenuPinnedAll({
	children,
	isSelected,
	onClick,
	showDivider = true,
}: {
	children: ReactNode;
	isSelected: boolean;
	onClick: () => void;
	showDivider?: boolean;
}) {
	return (
		<Box flexShrink={0}>
			<Box px="6px" pt="6px" pb="6px">
				<CheckboxMenuRow isSelected={isSelected} onClick={onClick}>
					{children}
				</CheckboxMenuRow>
			</Box>
			{showDivider ? (
				<Box borderBottom="1px solid" borderColor={checkboxMenuColors.divider} />
			) : null}
		</Box>
	);
}

export function CheckboxMenuFooter({
	selectedCount,
	totalCount,
	onClear,
}: {
	selectedCount: number;
	totalCount: number;
	onClear: () => void;
}) {
	return (
		<Flex
			align="center"
			justify="space-between"
			borderTop="1px solid"
			borderColor={checkboxMenuColors.divider}
			px="10px"
			py="8px"
		>
			<Text fontSize="11px" fontWeight={400} lineHeight="11px" color={checkboxMenuColors.muted}>
				{selectedCount} of {totalCount} selected
			</Text>
			<Text
				as="button"
				type="button"
				fontSize="11px"
				fontWeight={600}
				lineHeight="11px"
				color={checkboxMenuColors.brand}
				cursor="pointer"
				onClick={onClear}
			>
				Clear all
			</Text>
		</Flex>
	);
}

/**
 * Reset|Cancel + Apply pair that closes a staged filter menu. Menus that commit
 * on click (LocationSelector, VendorSelector) use `CheckboxMenuFooter` instead —
 * the footer is what tells you which commit model you're in.
 */
export function MenuFooterActions({
	onSecondary,
	onApply,
	secondaryLabel = 'Reset',
	applyLabel = 'Apply',
}: {
	onSecondary: () => void;
	onApply: () => void;
	secondaryLabel?: string;
	applyLabel?: string;
}) {
	return (
		<Flex gap="8px" p="12px" borderTop="1px solid" borderColor={checkboxMenuColors.divider}>
			<Button size="filter" variant="outlineGray" onClick={onSecondary} flex={1} fontWeight="medium">
				{secondaryLabel}
			</Button>
			<Button size="filter" onClick={onApply} flex={1} fontWeight="medium">
				{applyLabel}
			</Button>
		</Flex>
	);
}

export interface CheckboxMenuOption {
	value: string;
	label: string;
	isDisabled?: boolean;
}

export interface SearchableCheckboxMenuProps {
	options: CheckboxMenuOption[];
	value: string[];
	onChange: (value: string[]) => void;
	label?: ReactNode;
	searchPlaceholder?: string;
	emptyTriggerLabel?: string;
	width?: string | number;
	/** Render search + list + footer only (parent already owns the popover). */
	embed?: boolean;
	/** Extra rows inside the scrolling list (below a pinned All row). */
	header?: ReactNode;
	/** Pinned "All …" row above the list divider. Selected when `value` is empty. */
	pinnedAllLabel?: string;
	/** Hairline under the pinned All row. Defaults on when `pinnedAllLabel` is set. */
	showAllDivider?: boolean;
	/** Keep an existing token-remove affordance on the trigger. */
	removableTokens?: boolean;
	showTriggerTokens?: boolean;
	maxListHeight?: CSSProperties['maxHeight'];
	isDisabled?: boolean;
	/** Non-scrolling content between search and the list. */
	belowSearch?: ReactNode;
	/** Text shown before selected tokens, e.g. "Only" for an exact flag match. */
	triggerPrefix?: string;
}

export function SearchableCheckboxMenu({
	options,
	value,
	onChange,
	label,
	searchPlaceholder = 'Search...',
	emptyTriggerLabel,
	width = '100%',
	embed = false,
	header,
	pinnedAllLabel,
	showAllDivider,
	removableTokens = false,
	showTriggerTokens = true,
	maxListHeight = '236px',
	isDisabled,
	belowSearch,
	triggerPrefix,
}: SearchableCheckboxMenuProps) {
	const { isOpen, onToggle, onClose } = useDisclosure();
	const [query, setQuery] = useState('');

	const selectedSet = useMemo(() => new Set(value), [value]);

	const filtered = useMemo(() => {
		if (!query.trim()) return options;
		const q = query.toLowerCase();
		return options.filter((o) => o.label.toLowerCase().includes(q));
	}, [options, query]);

	const tokenLabels = useMemo(
		() =>
			value
				.map((v) => options.find((o) => o.value === v)?.label)
				.filter((v): v is string => Boolean(v)),
		[value, options],
	);

	const toggle = (optionValue: string) => {
		const next = selectedSet.has(optionValue)
			? value.filter((v) => v !== optionValue)
			: [...value, optionValue];
		onChange(next);
	};

	const allSelected = value.length === 0;
	const pinDivider = showAllDivider ?? Boolean(pinnedAllLabel);

	const tokenStrip = showTriggerTokens ? (
		<Flex wrap="wrap" gap="5px" px="10px" pt="8px" pb="4px" align="center">
			{tokenLabels.length > 0
				? (
					<>
						{triggerPrefix ? (
							<Text
								fontSize={checkboxMenuTriggerMetrics.fontSize}
								fontWeight={checkboxMenuTriggerMetrics.fontWeight}
								lineHeight="13px"
								color={checkboxMenuColors.input}
								py="4px"
							>
								{triggerPrefix}
							</Text>
						) : null}
						{tokenLabels.map((token, i) => (
							<TokenChip key={`${token}-${i}`}>{token}</TokenChip>
						))}
					</>
				)
				: (
						<Text
							fontSize={checkboxMenuTriggerMetrics.fontSize}
							fontWeight={checkboxMenuTriggerMetrics.fontWeight}
							lineHeight="13px"
							color={checkboxMenuColors.input}
							py="4px"
						>
							{emptyTriggerLabel ?? pinnedAllLabel ?? 'Select'}
						</Text>
					)}
		</Flex>
	) : null;

	const body = (
		<>
			{embed ? tokenStrip : null}
			<CheckboxMenuSearch value={query} onChange={setQuery} placeholder={searchPlaceholder} />
			{belowSearch}
			{pinnedAllLabel ? (
				<CheckboxMenuPinnedAll
					isSelected={allSelected}
					onClick={() => onChange([])}
					showDivider={pinDivider}
				>
					{pinnedAllLabel}
				</CheckboxMenuPinnedAll>
			) : null}
			<CheckboxMenuList maxH={maxListHeight}>
				{header}
				{filtered.length === 0 ? (
					<Text fontSize={checkboxMenuTriggerMetrics.fontSize} color={checkboxMenuColors.muted} px="10px" py="8px">
						No results
					</Text>
				) : (
					filtered.map((opt) => (
						<CheckboxMenuRow
							key={opt.value}
							isSelected={selectedSet.has(opt.value)}
							isDisabled={opt.isDisabled}
							onClick={() => toggle(opt.value)}
						>
							{opt.label}
						</CheckboxMenuRow>
					))
				)}
			</CheckboxMenuList>
			<CheckboxMenuFooter
				selectedCount={value.length}
				totalCount={options.length}
				onClear={() => onChange([])}
			/>
		</>
	);

	if (embed) {
		return <Box bg="white" overflow="hidden">{body}</Box>;
	}

	return (
		<Box w={width}>
			{label ? <CheckboxMenuLabel>{label}</CheckboxMenuLabel> : null}
			<Popover
				isOpen={isOpen}
				onClose={() => {
					setQuery('');
					onClose();
				}}
				placement="bottom-start"
				gutter={8}
				matchWidth
			>
				<PopoverTrigger>
					<Box>
						<CheckboxMenuTrigger
							isOpen={isOpen}
							tokens={showTriggerTokens ? tokenLabels : undefined}
							emptyLabel={emptyTriggerLabel}
							triggerPrefix={tokenLabels.length > 0 ? triggerPrefix : undefined}
							onClick={isDisabled ? undefined : onToggle}
							onRemoveToken={
								removableTokens
									? (index: number) => {
											const next = value.filter((_, i) => i !== index);
											onChange(next);
										}
									: undefined
							}
						/>
					</Box>
				</PopoverTrigger>
				<Portal>
					<PopoverContent w="100%" sx={checkboxMenuPopoverContentSx}>
						<PopoverBody p={0}>{body}</PopoverBody>
					</PopoverContent>
				</Portal>
			</Popover>
		</Box>
	);
}

export default SearchableCheckboxMenu;
